import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Card, CardContent, Chip, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Alert, CircularProgress, Grid,
    InputAdornment, IconButton, Switch,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import { apiGet, apiPost } from 'app/Services/ApiService';
import { apiUrls } from 'common/ApiUrls';
import { PrivateRoutesContext } from 'app/PrivateRoutesContext';
import LoaderAnimation from 'pages/LoaderAnimation';
import ConfirmationDialog from 'components/ConfirmationDialog';

// ── constants ────────────────────────────────────────────────────────────────

const FIELD_LABELS = {
    baseUrl:      'Base API URL',
    clientId:     'Client ID',
    clientSecret: 'Client Secret',
    privateKey:   'Private Key',
};

const AUTH_META = {
    'secrets':     { badge: 'OAuth',   color: 'info' },
    'private-key': { badge: 'API-KEY', color: 'warning' },
};

const EMPTY_FORM = {
    baseUrl: '', clientId: '', clientSecret: '', privateKey: '',
    isAuthenticated: false,
};

function requiredFields(authType) {
    if (authType === 'secrets')     return ['baseUrl', 'clientId', 'clientSecret'];
    if (authType === 'private-key') return ['baseUrl', 'privateKey'];
    return ['baseUrl'];
}

// ── component ────────────────────────────────────────────────────────────────

export default function PayrollVendor({ isReadonly }) {
    useContext(PrivateRoutesContext); // keep context subscription
    const companyId = localStorage.getItem('selectedCompanyId');

    // list
    const [integrations, setIntegrations]               = useState([]);
    const [loadingIntegrations, setLoadingIntegrations] = useState(true);

    // modal
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [modalOpen, setModalOpen]                     = useState(false);
    const [detailLoading, setDetailLoading]             = useState(false);
    const [openedViaToggle, setOpenedViaToggle]         = useState(false);

    // form
    const [formData, setFormData]             = useState(EMPTY_FORM);
    const [touched, setTouched]               = useState({});
    const [showSecret, setShowSecret]         = useState(false);
    const [showPrivateKey, setShowPrivateKey] = useState(false);

    // test / save
    const [testStatus, setTestStatus]   = useState(null);    // null|'loading'|'success'|'error'
    const [testMessage, setTestMessage] = useState('');
    const [saving, setSaving]           = useState(false);

    // disable confirmation
    const [confirmDisableOpen, setConfirmDisableOpen]             = useState(false);
    const [pendingDisableIntegration, setPendingDisableIntegration] = useState(null);
    const [toggling, setToggling]                                 = useState(false);

    // ── data ─────────────────────────────────────────────────────────────────

    useEffect(() => { fetchIntegrations(); }, []);

    const fetchIntegrations = () => {
        setLoadingIntegrations(true);
        apiGet(apiUrls.getPayrollIntegrations(companyId))
            .then(res => setIntegrations(res.data))
            .catch(() => setIntegrations([]))
            .finally(() => setLoadingIntegrations(false));
    };

    // ── validation helpers ────────────────────────────────────────────────────

    const visible = requiredFields(selectedIntegration?.authType);

    const fieldErrors = Object.fromEntries(
        visible.map(f => [
            f,
            touched[f] && !formData[f]?.trim() ? `${FIELD_LABELS[f]} is required` : '',
        ])
    );

    const isFormFilled = visible.every(f => formData[f]?.trim());

    // ── handlers ─────────────────────────────────────────────────────────────

    const openForm = (integration, viaToggle = false) => {
        setOpenedViaToggle(viaToggle);
        setSelectedIntegration(integration);
        setFormData(EMPTY_FORM);
        setTouched({});
        setTestStatus(null);
        setTestMessage('');
        setShowSecret(false);
        setShowPrivateKey(false);
        setDetailLoading(true);
        setModalOpen(true);

        apiGet(apiUrls.getCompanyPayrollIntegration(companyId, integration.id))
            .then(res => {
                const d = res.data;
                setFormData({
                    baseUrl:         d.apiBaseUrl      || '',
                    clientId:        d.clientId        || '',
                    clientSecret:    d.clientSecret    || '',
                    privateKey:      d.apiKey          || '',
                    isAuthenticated: d.isAuthenticated ?? false,
                });
            })
            .catch(() => {}) // 404 / no record → keep form empty (new integration)
            .finally(() => setDetailLoading(false));
    };

    // Toggling ON opens the credential form; toggling OFF shows the disable confirmation.
    const handleToggle = (integration, willEnable) => {
        if (willEnable) {
            openForm(integration, true);
        } else {
            setConfirmDisableOpen(true);
            setPendingDisableIntegration(integration);
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedIntegration(null);
        setTouched({});
        setTestStatus(null);
        setTestMessage('');
        setOpenedViaToggle(false);
    };

    // Credential edits invalidate a previous successful test
    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value, isAuthenticated: false }));
        setTestStatus(null);
        setTestMessage('');
    };

    const handleBlur = (field) => () =>
        setTouched(prev => ({ ...prev, [field]: true }));

    const touchAllVisible = () => {
        const all = Object.fromEntries(visible.map(f => [f, true]));
        setTouched(prev => ({ ...prev, ...all }));
    };

    const buildTestPayload = () => {
        const { authType, id: payrollIntegrationId } = selectedIntegration;
        const base = { companyId, payrollIntegrationId, apiBaseUrl: formData.baseUrl, authType };
        if (authType === 'secrets')     return { ...base, clientId: formData.clientId, clientSecret: formData.clientSecret };
        if (authType === 'private-key') return { ...base, apiKey: formData.privateKey };
        return base;
    };

    const buildSavePayload = () => {
        const { authType, id: payrollIntegrationId } = selectedIntegration;
        const base = {
            companyId, payrollIntegrationId,
            apiBaseUrl:      formData.baseUrl,
            isAuthenticated: formData.isAuthenticated,
        };
        if (authType === 'secrets')     return { ...base, clientId: formData.clientId, clientSecret: formData.clientSecret };
        if (authType === 'private-key') return { ...base, apiKey: formData.privateKey };
        return base;
    };

    const handleTestConnection = () => {
        touchAllVisible();
        if (!isFormFilled) return;
        setTestStatus('loading');
        apiPost(apiUrls.testPayrollIntegration(), buildTestPayload())
            .then(res => {
                const passed = res.data === true || res.data?.success === true;
                if (passed) {
                    setFormData(prev => ({ ...prev, isAuthenticated: true }));
                    setTestStatus('success');
                    setTestMessage(res.data?.message || 'Connection successful.');
                } else {
                    setFormData(prev => ({ ...prev, isAuthenticated: false }));
                    setTestStatus('error');
                    setTestMessage(res.data?.message || 'Connection failed. Please verify your credentials.');
                }
            })
            .catch(err => {
                setFormData(prev => ({ ...prev, isAuthenticated: false }));
                setTestStatus('error');
                setTestMessage(
                    err?.response?.data?.message || 'Connection failed. Please verify your credentials.'
                );
            });
    };

    const handleSave = () => {
        touchAllVisible();
        if (!isFormFilled) return;
        setSaving(true);
        const payload = {
            ...buildSavePayload(),
            ...(openedViaToggle ? { isActive: true } : {}),
        };
        apiPost(apiUrls.savePayrollIntegration(), payload)
            .then(() => {
                toast.success(openedViaToggle ? 'Integration enabled.' : 'Integration saved successfully.');
                handleModalClose();
                fetchIntegrations();
            })
            .catch(err => {
                toast.error(
                    err?.response?.data?.message || 'Failed to save integration. Please try again.'
                );
            })
            .finally(() => setSaving(false));
    };

    const handleConfirmDisable = () => {
        setToggling(true);
        const { id: payrollIntegrationId } = pendingDisableIntegration;
        apiPost(apiUrls.toggleCompanyIntegration(companyId, payrollIntegrationId), {
            isEnabled: false,
        })
            .then(() => {
                toast.success('Integration disabled.');
                setConfirmDisableOpen(false);
                setPendingDisableIntegration(null);
                fetchIntegrations();
            })
            .catch(err => {
                toast.error(
                    err?.response?.data?.message || 'Failed to disable integration.'
                );
            })
            .finally(() => setToggling(false));
    };

    const handleCancelDisable = () => {
        setConfirmDisableOpen(false);
        setPendingDisableIntegration(null);
    };

    // ── shared TextField props factory ────────────────────────────────────────

    const fieldProps = (name, extra = {}) => ({
        label:      FIELD_LABELS[name],
        value:      formData[name],
        onChange:   handleChange(name),
        onBlur:     handleBlur(name),
        error:      !!fieldErrors[name],
        helperText: fieldErrors[name] || ' ',   // ' ' keeps height stable
        fullWidth:  true,
        size:       'small',
        disabled:   isReadonly,
        ...extra,
    });

    const revealAdornment = (shown, toggle) => ({
        InputProps: {
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton onClick={() => toggle(v => !v)} edge="end" size="small">
                        {shown ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                </InputAdornment>
            ),
        },
    });

    // ── render ────────────────────────────────────────────────────────────────

    if (loadingIntegrations) {
        return <Box sx={{ position: 'relative', height: 200 }}><LoaderAnimation /></Box>;
    }

    const authMeta = selectedIntegration ? (AUTH_META[selectedIntegration.authType] ?? {}) : {};
    const canSave  = isFormFilled && !saving && !isReadonly
                    && (!openedViaToggle || testStatus === 'success');
    const modalTitle = openedViaToggle
        ? `Enable ${selectedIntegration?.name}`
        : `Configure ${selectedIntegration?.name}`;

    return (
        <>
            {/* ── Integration cards ── */}
            <Box sx={{ p: 1 }}>
                {integrations.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
                        No payroll integrations are currently enabled.
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {integrations.map(integration => {
                            const meta = AUTH_META[integration.authType] ?? {};
                            return (
                                <Grid item key={integration.id} xs={12} sm={6} md={4} lg={3}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            opacity: isReadonly ? 0.65 : 1,
                                        }}
                                    >
                                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                            {integration.logoUrl ? (
                                                <Box
                                                    component="img"
                                                    src={integration.logoUrl}
                                                    alt={integration.name}
                                                    sx={{
                                                        width: 64, height: 64,
                                                        objectFit: 'contain', mb: 1.5,
                                                        display: 'block', mx: 'auto',
                                                    }}
                                                />
                                            ) : (
                                                <Box sx={{
                                                    width: 64, height: 64, borderRadius: 2,
                                                    mx: 'auto', mb: 1.5,
                                                    bgcolor: 'primary.veryLight',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <Typography variant="h5" color="primary">
                                                        {integration.name?.charAt(0).toUpperCase()}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                                                {integration.name}
                                            </Typography>

                                            {meta.badge && (
                                                <Chip
                                                    label={meta.badge}
                                                    color={meta.color}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}

                                            {/* Enable / disable toggle — click is isolated from card click */}
                                            <Box
                                                sx={{
                                                    display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', gap: 0.5, mt: 1.5,
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color={integration.isActive ? 'success.main' : 'text.secondary'}
                                                >
                                                    {integration.isActive ? 'Enabled' : 'Disabled'}
                                                </Typography>
                                                <Switch
                                                    size="small"
                                                    checked={!!integration.isActive}
                                                    onChange={(e) => handleToggle(integration, e.target.checked)}
                                                    disabled={isReadonly}
                                                    color="primary"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>

            {/* ── Disable confirmation ── */}
            <ConfirmationDialog
                open={confirmDisableOpen}
                title="Disable Integration"
                message="Are you sure you want to disable this integration?"
                onClose={handleCancelDisable}
                onConfirm={handleConfirmDisable}
                confirmLabel="Disable"
                processing={toggling}
                processingLabel="Disabling..."
            />

            {/* ── Config / Enable modal ── */}
            <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {modalTitle}
                    {authMeta.badge && (
                        <Chip
                            label={authMeta.badge}
                            color={authMeta.color}
                            size="small"
                            variant="outlined"
                        />
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    {detailLoading ? (
                        <Box sx={{ position: 'relative', height: 120 }}><LoaderAnimation /></Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', pt: 1 }}>

                            {/* Base API URL — always visible */}
                            <TextField
                                {...fieldProps('baseUrl', { placeholder: 'https://api.example.com', sx: { mb: 0.5 } })}
                            />

                            {/* OAuth (secrets) fields */}
                            {selectedIntegration?.authType === 'secrets' && (
                                <>
                                    <TextField {...fieldProps('clientId', { sx: { mb: 0.5 } })} />
                                    <TextField
                                        {...fieldProps('clientSecret', {
                                            type: showSecret ? 'text' : 'password',
                                            sx: { mb: 0.5 },
                                            ...revealAdornment(showSecret, setShowSecret),
                                        })}
                                    />
                                </>
                            )}

                            {/* API-KEY (private-key) field */}
                            {selectedIntegration?.authType === 'private-key' && (
                                <TextField
                                    {...fieldProps('privateKey', {
                                        type: showPrivateKey ? 'text' : 'password',
                                        sx: { mb: 0.5 },
                                        ...revealAdornment(showPrivateKey, setShowPrivateKey),
                                    })}
                                />
                            )}

                            {/* IsAuthenticated — readonly status badge */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Authentication Status:
                                </Typography>
                                <Chip
                                    label={formData.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
                                    color={formData.isAuthenticated ? 'success' : 'default'}
                                    size="small"
                                />
                            </Box>

                            {/* Test Connection row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleTestConnection}
                                    disabled={!isFormFilled || testStatus === 'loading' || isReadonly}
                                    startIcon={
                                        testStatus === 'loading'
                                            ? <CircularProgress size={16} color="inherit" />
                                            : null
                                    }
                                >
                                    Test Connection
                                </Button>
                                {testStatus === 'success' && (
                                    <Alert severity="success" sx={{ py: 0, flex: 1 }}>
                                        {testMessage}
                                    </Alert>
                                )}
                                {testStatus === 'error' && (
                                    <Alert severity="error" sx={{ py: 0, flex: 1 }}>
                                        {testMessage}
                                    </Alert>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button variant="outlined" onClick={handleModalClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!canSave}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {openedViaToggle ? 'Enable' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
